import React from "react";
import { useLocation, useQueryParams } from "hooks";
import { Location } from "history";
import { Auth } from "types";
import chmln from "@chamaeleonidae/chmln";
import { isEmpty } from "lodash-es";
import { arePathsEqual } from "./operations";
import { CHAM_TOKEN } from "./constants";

export const useScrollToTop = (): void => {
  const { pathname } = useLocation();
  const [params] = useQueryParams();
  const page = params.get<{ number?: number; size?: number }>("page", {});

  React.useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [pathname, page.number, page.size]);
};

const buildLocationFromUrl = (url?: string | URL | null): Location<unknown> => {
  const currentHref = window.location.href;
  const parsedUrl = new URL(url?.toString() || currentHref, currentHref);

  return {
    pathname: parsedUrl.pathname,
    search: parsedUrl.search,
    hash: parsedUrl.hash,
    key: "",
    state: null,
  };
};

export const useLocationBlocker = (): void => {
  React.useEffect(() => {
    let isEmbedded = false;
    try {
      isEmbedded = window.self !== window.top;
    } catch {
      isEmbedded = true;
    }

    const pushState = window.history.pushState.bind(window.history);
    const replaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void => {
      const currentLocation = buildLocationFromUrl();
      const nextLocation = buildLocationFromUrl(url);

      if (arePathsEqual(currentLocation, nextLocation)) {
        return;
      }

      if (isEmbedded) {
        // In iframe mode avoid growing browser history so Back returns to LMS page.
        replaceState(data, unused, url);
        return;
      }

      pushState(data, unused, url);
    };

    return () => {
      window.history.pushState = pushState;
    };
  }, []);
};

const extractDistrictId = (url: string): string => {
  const match = url.match(/n(\d+)d(\d+)\.acceleratelearning\.com/);
  return match ? match[2] : "";
};

export const useChameleon = (auth: Auth): void => {
  const mountRef = React.useRef(false);

  React.useEffect(() => {
    if (isEmpty(auth.data)) {
      return;
    }

    if (!mountRef.current) {
      mountRef.current = true;

      const { user, profile, school } = auth.data;

      const roles = user?.roles?.join("");
      const scopeGroups = school?.scopeGroups
        ?.map((scopeGroup) => scopeGroup?.shortName)
        .join(",");

      if (!profile) {
        // todo: just for debugging, will remove in my next PR
        // eslint-disable-next-line no-console
        console.log("Profile is missing, skipping chameleon init");
        return;
      }

      const district = profile?.dmUrl || "";

      chmln.init(CHAM_TOKEN, { fastUrl: "https://fast.chameleon.io/" });

      chmln.identify(user?.id || "", {
        email: user?.email || "",
        name: `${profile?.firstName} ${profile?.lastName}`,
        role: roles,
        product: scopeGroups || "",
        district: extractDistrictId(district),
        state: school.state || "",
        version: "STEMscopes 3.5",
      });
    }
  }, [auth]);
};
